import { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { useNavigate, useLocation } from "react-router-dom";
import debounce from "lodash.debounce";
import Pagination from "@mui/material/Pagination";

const apiBaseURL = process.env.REACT_APP_API_URL || `${window.location.origin}`;

const options = {
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
  day: "numeric",
  month: "long",
  year: "numeric",
};

export const LeaderBoard = () => {
  const navigate = useNavigate();
  const [value, setValue] = useState("1");
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;
  const [storedData, setStoredData] = useState([]);
  const location = useLocation();
  const data = location.state || {};

  const backURL = sessionStorage.getItem("backURLs") || "";

  useEffect(() => {
    return () => {
      sessionStorage.removeItem("leaderBoardCurrentMobileNumber");
    };
  }, []);

  useEffect(() => {
    getLeaderboardData(pageNumber, value, searchQuery || "empty");
    if (data && data?.mobileNo && data?.ottsmsid) {
      handleLeaderboardAllClick(data?.mobileNo, data?.ottsmsid);
    }
  }, [pageNumber, value, searchQuery]);

  const handleLeaderboardAllClick = (mobileNo, ottsmsid) => {
    window?.dataLayer.push({
      event: "leader_board_click",
      mobile_number: mobileNo,
      ott_sms_id: ottsmsid,
      date: new Date(Date.now())?.toLocaleString("en-US", options),
    });
  };

  const getLeaderboardData = async (page, tabValue, searchData) => {
    setLoading(true);
    try {
      let searchValue = searchData?.trim() || "empty";
      const response = await fetch(
        `${apiBaseURL}/api/IPLContestWatcho/GetWeeklyLeaderboard?Mobile=${
          data?.mobileNo || "empty"
        }&name=${searchValue}&IsWeeklyLeaderboard=${tabValue}&pageNumber=${page}&pageSize=${pageSize}`,
        { method: "GET" }
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response?.status}`);
      const responseData = await response.json();
      setLeaderboardData(responseData?.result || []);
      const currentUser = responseData?.result?.filter(
        (q) =>
          q?.number === sessionStorage.getItem("leaderBoardCurrentMobileNumber")
      );
      if (page === 1 && searchValue === "empty") {
        setStoredData(currentUser);
      }
      const calculatedTotalPages = Math.max(
        1,
        Math.ceil((responseData?.totalCount || 0) / pageSize)
      );
      setTotalPages(calculatedTotalPages);
    } catch (error) {
      console.error("Error fetching leaderboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setPageNumber(1);
  };

  const handlePageChange = (event, value) => {
    setPageNumber(value);
  };

  const handleSearch = useCallback(
    debounce((searchDataItems) => {
      setSearchQuery(searchDataItems);
      setPageNumber(1);
    }, 300),
    []
  );

  const handleBack = () => {
    const path =
      backURL === "IPLContestWatcho"
        ? `/${backURL}`
        : `/IPLContestWatcho?key=${backURL}`;
    navigate(path);
  };

  return (
    <div className="container">
      {!backURL ? (
        ""
      ) : (
        <div className="back my-3">
          <button
            style={{
              backgroundColor: "transparent",
              border: "none",
              cursor: "pointer",
            }}
            onClick={handleBack}
          >
            <i className="fa-solid fa-arrow-left mr-2"></i> Back
          </button>
        </div>
      )}

      <div className="leaderBoardInnerWrapper" style={{ height: "auto" }}>
        <h1>Check Winner’s Leaderboard!</h1>
        {storedData?.length > 0 ? (
          <div className="selfPoints">
            <div className="selfPointsInnerContainer">
              <table className="table table-borderless mb-0">
                <tbody>
                  {storedData?.map((item) => (
                    <tr key={item?.id}>
                      <td className="pl-3">{item?.rank || "N/A"}</td>
                      <td>
                        {item?.winnerName
                          ? item?.winnerName.length > 10
                            ? item?.winnerName.toUpperCase().slice(0, 15) +
                              "..."
                            : item?.winnerName.toUpperCase()
                          : "N/A"}{" "}
                        <br />
                        {item?.mobileNumber || ""}
                      </td>
                      <td className="pr-3">{item?.scoredPoint ?? "0"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          ""
        )}

        <div className="leaderBoardContainer mt-3">
          <Box sx={{ width: "100%", typography: "body1" }}>
            <TabContext value={value}>
              <Box>
                <div className="d-md-flex justify-content-between">
                  <TabList
                    indicatorColor="transparent"
                    className="justify-content-center"
                    onChange={handleChange}
                    aria-label="Leaderboard Tabs"
                  >
                    <Tab label="This Week" value="1" />
                    <Tab label="Tournament" value="0" />
                  </TabList>

                  <div className="searchContainer">
                    <input
                      type="text"
                      placeholder="Search Name"
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                  </div>
                </div>
              </Box>

              <TabPanel
                value="1"
                sx={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}
              >
                <LeaderboardTable
                  leaderboardData={leaderboardData}
                  loading={loading}
                />
              </TabPanel>

              <TabPanel
                value="0"
                sx={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}
              >
                <LeaderboardTable
                  leaderboardData={leaderboardData}
                  loading={loading}
                />
              </TabPanel>
            </TabContext>
          </Box>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination
                count={totalPages}
                page={pageNumber}
                onChange={handlePageChange}
                color="primary"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LeaderboardTable = ({ leaderboardData, loading }) => {
  return (
    <>
      <table className="table table-borderless mb-0">
        <thead className="sticky-top123">
          <tr className="text-uppercase">
            <th className="pl-3">Rank</th>
            <th>Winner Name</th>
            <th className="pr-3">Points</th>
          </tr>
        </thead>
        <tbody>
          {leaderboardData.length === 0 && !loading ? (
            <tr>
              <td colSpan="3" className="text-center">
                No Winners Found.
              </td>
            </tr>
          ) : (
            leaderboardData.map((data, index) => (
              <tr key={data?.id || index}>
                <td className="pl-3">{data?.rank || "N/A"}</td>
                <td>
                  {data?.winnerName?.toUpperCase() || "Unknown"} <br />{" "}
                  {data?.mobileNumber || ""}
                </td>
                <td className="pr-3">{data?.scoredPoint ?? "0"}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {loading && <p className="text-center">Loading more...</p>}
    </>
  );
};
